// Lib
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Button as RomaButton, UtilityButton } from '@milanote/roma';
import { EditorToolbar } from '@milanote/roma/application';

// Utils
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformIpad } from '../../../../platform/utils/platformDetailsUtils';

// Components
import DrawingEditorColorToolV2 from './DrawingEditorColorToolV2';
import DrawingEditorStrokeSizeToolV2 from './DrawingEditorStrokeSizeToolV2';
import DrawingEditorToolbarIPadPenTools from './DrawingEditorToolbarIPadPenTools';
import { Cursor03Icon, EraserIcon, Pencil02Icon, ReverseLeft02Icon, ReverseRight02Icon } from '@milanote/roma-icons';
import { useTranslator } from '../../../../components/i18n/useTranslator';
import CancelDiscardButton from '../../../image/modal/v2/toolbar/CancelDiscardButton';

// Hooks
import usePencilDoubleTap from './usePencilDoubleTap';
import useTouchBlockedPointerEvent from './useTouchBlockedPointerEvent';

// Constants
import { TOOL_MODES } from '../drawingEditorConstants';
import { CAPACITOR_APPLE_PENCIL_PLUGIN_NAME } from '../../../../../capacitor_plugins/pluginConstants';
import { stopDefaultAndPropagation } from '../../../../utils/domUtil';

type ToolMode = (typeof TOOL_MODES)[keyof typeof TOOL_MODES];

export type DrawingEditorToolbarV2Props = {
    isDrawing?: boolean;
    isEditingExistingDrawing?: boolean;
    toolMode: ToolMode;
    toolModeState?: {
        hasSelection?: boolean;
    };
    colorPopupOpen: boolean;
    strokePopupOpen: boolean;
    strokeSize: number;
    strokeColor: string;
    defaultColors: string[];
    handleSetStrokeColor: (color: string) => void;
    handleSetStrokeSize: (strokeSize: number) => void;
    handleDiscardButton: () => void;
    handleSaveButton: () => void;
    setToolMode: (toolMode: ToolMode) => void;
    setColorPopupOpen: (isOpen: boolean) => void;
    setCustomColorInputOpen: (isOpen: boolean) => void;
    setStrokePopupOpen: (isOpen: boolean) => void;
    canUndo: boolean;
    canRedo: boolean;
    undoPath: () => void;
    redoPath: () => void;
    isPenMode: boolean;
    setIsPenMode: (isPenMode: boolean) => void;
    isTouchEventInToolbarBlocked: boolean;
};

type DrawingEditorToolbarV2ToolModeButtonProps = {
    currentToolMode: ToolMode;
    toolMode: ToolMode;
    setToolMode: (toolMode: ToolMode) => void;
    isPenMode: boolean;
    children: React.ReactNode;
    name: string;
};

// If we're currently drawing, then there's no need to update the toolbar
const arePropsEqual = (_prevProps: DrawingEditorToolbarV2Props, nextProps: DrawingEditorToolbarV2Props) =>
    !!nextProps.isDrawing;

const DrawingEditorToolbarV2ToolModeButton: React.FC<DrawingEditorToolbarV2ToolModeButtonProps> = ({
    currentToolMode,
    toolMode,
    setToolMode,
    isPenMode,
    children,
    name,
}) => {
    const updateToolMode = React.useCallback(() => setToolMode(toolMode), [toolMode, setToolMode]);
    const onButtonPointerUp = useTouchBlockedPointerEvent(isPenMode, updateToolMode);

    return (
        <UtilityButton
            isSelected={currentToolMode === toolMode}
            onPointerUp={onButtonPointerUp}
            onDoubleClick={stopDefaultAndPropagation}
            tooltip={{ title: name }}
        >
            {children}
        </UtilityButton>
    );
};

/**
 * Roma-styled (v2) variant of the drawing editor toolbar. Reuses the existing pen/touch-correct
 * controls (tool-mode buttons, colour tool, undo/redo, save/discard) inside a Roma EditorToolbar,
 * swapping only the stroke tool for its v2 (new-icon) version. Gated behind imageModalV2
 * via DrawingEditorToolbarSwitch so v1 stays untouched for the slow rollout.
 */
const DrawingEditorToolbarV2 = React.memo(function DrawingEditorToolbarV2({
    shellClassName = 'DrawingEditorToolbarV2 leading-none',

    toolMode,
    toolModeState,

    colorPopupOpen,
    strokePopupOpen,

    strokeSize,
    strokeColor,
    defaultColors,

    handleSetStrokeColor,
    handleSetStrokeSize,
    handleDiscardButton,
    handleSaveButton,

    setToolMode,
    setColorPopupOpen,
    setCustomColorInputOpen,
    setStrokePopupOpen,

    canUndo,
    canRedo,
    undoPath,
    redoPath,

    isPenMode,
    setIsPenMode,
    isTouchEventInToolbarBlocked,
}: DrawingEditorToolbarV2Props & { shellClassName?: string }) {
    const translate = useTranslator('popup', 'drawing');

    if (Capacitor.isPluginAvailable(CAPACITOR_APPLE_PENCIL_PLUGIN_NAME)) {
        usePencilDoubleTap({ toolMode, setToolMode, setColorPopupOpen, setStrokePopupOpen });
    }

    const hasSelection = toolModeState?.hasSelection;
    const isSelectMode = toolMode === TOOL_MODES.SELECT;
    const disablePathTools = toolMode === TOOL_MODES.ERASE || (isSelectMode && !hasSelection);
    // In select mode we mutate saved SVG path transforms directly; re-deriving freehand stroke
    // width from transformed selection data is intentionally disabled as a trade-off.
    const disableStrokeSizeTool = disablePathTools || isSelectMode;

    const onUndo = useTouchBlockedPointerEvent(isPenMode, undoPath);
    const onRedo = useTouchBlockedPointerEvent(isPenMode, redoPath);
    const onDiscard = useTouchBlockedPointerEvent(isTouchEventInToolbarBlocked, handleDiscardButton);
    const onSave = useTouchBlockedPointerEvent(isTouchEventInToolbarBlocked, handleSaveButton);

    return (
        <EditorToolbar.Shell className={shellClassName}>
            <EditorToolbar.List>
                <DrawingEditorToolbarV2ToolModeButton
                    name={translate('tools.pen')}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.DRAW}
                    setToolMode={setToolMode}
                >
                    <Pencil02Icon />
                </DrawingEditorToolbarV2ToolModeButton>
                <DrawingEditorToolbarV2ToolModeButton
                    name={translate('tools.selectionTool')}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.SELECT}
                    setToolMode={setToolMode}
                >
                    <Cursor03Icon />
                </DrawingEditorToolbarV2ToolModeButton>
                <DrawingEditorToolbarV2ToolModeButton
                    name={translate('tools.eraser')}
                    isPenMode={isPenMode}
                    currentToolMode={toolMode}
                    toolMode={TOOL_MODES.ERASE}
                    setToolMode={setToolMode}
                >
                    <EraserIcon />
                </DrawingEditorToolbarV2ToolModeButton>

                <EditorToolbar.Divider />

                <DrawingEditorColorToolV2
                    isPenMode={isPenMode}
                    defaultColors={defaultColors}
                    disabled={disablePathTools}
                    keepPopupOpen={toolMode === TOOL_MODES.DRAW}
                    colorPopupOpen={colorPopupOpen}
                    setColorPopupOpen={setColorPopupOpen}
                    setCustomColorInputOpen={setCustomColorInputOpen}
                    strokeColor={strokeColor}
                    setStrokeColor={handleSetStrokeColor}
                />

                <DrawingEditorStrokeSizeToolV2
                    isPenMode={isPenMode}
                    disabled={disableStrokeSizeTool}
                    keepPopupOpen={toolMode === TOOL_MODES.SELECT}
                    strokePopupOpen={strokePopupOpen}
                    strokeSize={strokeSize}
                    setStrokeSize={handleSetStrokeSize}
                    setStrokePopupOpen={setStrokePopupOpen}
                />

                <EditorToolbar.Divider />

                <UtilityButton isDisabled={!canUndo} onPointerUp={onUndo} tooltip={{ title: translate('tools.undo') }}>
                    <ReverseLeft02Icon />
                </UtilityButton>
                <UtilityButton isDisabled={!canRedo} onPointerUp={onRedo} tooltip={{ title: translate('tools.redo') }}>
                    <ReverseRight02Icon />
                </UtilityButton>

                <EditorToolbar.Divider type="spacer" />

                <div className="flex shrink-0 items-center gap-sm">
                    <CancelDiscardButton showDiscard={canUndo} onPointerUp={onDiscard} />
                    <RomaButton onPointerUp={onSave}>{translate('save')}</RomaButton>
                </div>
            </EditorToolbar.List>

            {isPlatformIpad(platformSingleton) && (
                <DrawingEditorToolbarIPadPenTools
                    toolMode={toolMode}
                    isPenMode={isPenMode}
                    setIsPenMode={setIsPenMode}
                />
            )}
        </EditorToolbar.Shell>
    );
}, arePropsEqual);

export default DrawingEditorToolbarV2;

export const DrawingEditorToolbarV2WithTransitions: React.FC<DrawingEditorToolbarV2Props> = (props) => (
    <EditorToolbar
        isOpen
        autoFocus={false}
        restoreFocus={false}
        className="DrawingEditorToolbarV2 right-0 left-0 w-fit translate-x-0"
    >
        <DrawingEditorToolbarV2 {...props} shellClassName="leading-none" />
    </EditorToolbar>
);
